import { useQuery } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import toast from 'react-hot-toast';
import type { CodeMaster, Seller, Farmer, Gap } from '../types'

// このフックが最終的に返すデータの型
export interface MastersData {
  masters: CodeMaster[];
  sellers: Seller[];
  farmers: Farmer[];
  gaps: Gap[];
}

/**
 * 全てのマスターデータを一括で取得し、キャッシュするためのReact Queryフック
 */
export const useMastersQuery = (isLoggedIn: boolean, isReferenceMode: boolean = false) => {
  const api = useApiClient();

  return useQuery<MastersData, Error>({
    // モードによってキーを変える（キャッシュ汚染防止）
    queryKey: isReferenceMode ? ['masters_reference'] : ['masters'],

    queryFn: async () => {
      try {
        // ★ここを修正: 「売り手」だけURLを切り替える
        const sellersUrl = isReferenceMode
          ? '/masters/sellers/admin?includeDeleted=true' // 確認画面用
          : '/masters/sellers';                          // 通常用

        // ★重要: 生産者などは「今まで通り」のURLに固定する（変数にしない）
        // これで「実装していないAdminAPI」を叩いてエラーになるのを防ぎます
        const [
          mastersRes,
          sellersRes,
          farmersRes,
          gapsRes,
        ] = await Promise.all([
          api.get(`/Masters/codemaster`), // 変更なし
          api.get(sellersUrl),            // ここだけ切り替わる
          api.get(`/Masters/farmers`),    // 変更なし（固定）
          api.get(`/Masters/gaps`),
        ]);

        return {
          masters: mastersRes.data,
          sellers: sellersRes.data,
          farmers: farmersRes.data,
          gaps: gapsRes.data,
        };
      } catch (error) {
        console.error("Master fetch error:", error);
        toast.error("マスタデータの取得に失敗しました。");
        throw error;
      }
    },
    // ★★★ キャッシュ戦略の核心 ★★★
    // ログインしている場合にのみ、このクエリを有効にする
    enabled: isLoggedIn,
    // // staleTimeをInfinityに設定すると、一度取得したデータは
    // // ユーザーがブラウザをリロードするまで「新鮮」と見なされ、再取得が走らなくなる
    // staleTime: Infinity,
    // staleTimeを10分に設定。取得から10分間はデータを「新鮮」と見なし、
    // この間は画面を切り替えてもAPIの再実行は行われません。
    staleTime: 1000 * 60 * 5, // 5分
    // gcTime (旧cacheTime) をInfinityに設定すると、非アクティブになってもキャッシュがメモリから消えなくなる
    gcTime: Infinity,
    // エラー時に自動でリトライしない
    retry: false,
  });
};

